import * as TestPanel from '../app/testPanel'
import { globalState } from '@codeleap/store'
import { queryClient } from './api'
import { waitFor } from '@codeleap/utils'
import { IS_SSR } from '@/app'
import { useQuery } from '@tanstack/react-query'

type SharedSettings = {
  max_hurdle_archive_age?: number
  initial_lesson: number
  max_size_upload: number
  splash: {
    text: string
  }
  all_industries: {
    text: string
  }
  contribution: {
    title: string
  }
  signup: {
    hero: string
  }
  learn: {
    persona: {
      loadingTime: number
      onboardingHero: string
      onboardingDescription: string
    }
    emptyPlaceholderDescription: string
    emptyPlaceholderTitle: string
    notFoundDescription: string
    notFoundTitle: string
    placeholder: string
  }
  socialProviders: {
    apple: string
    google: string
    facebook: string
    linkedIn: string
  }
}

const store = globalState({} as SharedSettings, { persistKey: `SHARED_SETTINGS_${TestPanel.getEnv().server}` })

const fetchSharedSettingsFromSite = async (site: string): Promise<SharedSettings | null> => {
  const timestamp = Date.now()

  const isLocalhost = IS_SSR ? false : window.origin.includes('localhost')

  if (isLocalhost) {
    await waitFor(1000)
    return {
      learn: {
        placeholder: 'Search',
        notFoundTitle: 'Not found',
        notFoundDescription: 'Not found description',
        emptyPlaceholderTitle: 'Search',
        emptyPlaceholderDescription: 'Description',
        persona: {
          loadingTime: 1500,
          onboardingHero: 'Welcome to our learning platform!',
          onboardingDescription: 'Discover courses tailored to your interests and goals.'
        }
      },
      signup: {
        hero: 'Register to explore practical tips, inspiring stories, and a community of like-minded changemakers.'
      },
      splash: { text: 'Hello from admin!' },
      contribution: { title: 'FLAMENGO MENG\u00c3O' },
      all_industries: { text: 'Some of the biggest organisations around the world' },
      initial_lesson: 23,
      max_size_upload: 500,
      socialProviders: { apple: 'true', google: 'true', facebook: 'false', linkedIn: 'true' }
    }
  }

  const response = await fetch(`${site}/shared_settings.json?t=${timestamp}`, {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0'
    },
    credentials: 'omit'
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  return await response.json()
}

export async function fetchSharedSettings(): Promise<SharedSettings> {
  const url = TestPanel.getWebsiteUrl()

  try {
    const settings = await fetchSharedSettingsFromSite(url)

    if (settings) {
      store.set(settings)
      return settings
    }

    return store.get()
  } catch (error) {
    logger.error('Error fetching all shared settings:', error)
    return store.get()
  }
}

export async function getSharedSettings(): Promise<SharedSettings> {
  const cachedData = queryClient.client.getQueryData<SharedSettings>(['sharedSettings'])

  if (cachedData) {
    return cachedData
  }

  return queryClient.client.fetchQuery({
    queryKey: ['sharedSettings'],
    queryFn: fetchSharedSettings
  })
}

export function useSharedSettings() {
  const query = useQuery({
    queryKey: ['sharedSettings'],
    placeholderData: () => store.get(),
    queryFn: fetchSharedSettings,
    staleTime: Infinity
  })

  return {
    isUpdated: !query?.isPlaceholderData && query?.isSuccess,
    isLoaded: !!query?.data && !query?.isPlaceholderData,
    settings: query.data
  }
}
